import React, { useMemo, useState, useEffect } from 'react';
import {
  Plus,
  FileText,
  Database,
  RefreshCw,
  Pencil,
  Trash2,
  X,
  Search,
  Filter,
  Calendar,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Info,
  Zap,
  ScrollText,
  Activity,
  ListFilter,
  Download,
} from 'lucide-react';
import { useTheme } from '../../../../components/common/Theme/ThemeContext';
import AddEditScheduleModal, { ScheduleFormValues, SchedulePayload } from './AddEditScheduleModal';
import CapturedDataModal from './CapturedDataModal';
import ConfirmPopup from './ConfirmPopup';
import { ingestionService } from '../../../../components/api/ingestionService';

// --- Types ---
type ScheduleStatus = 'active' | 'running' | 'idle' | 'error';

interface Schedule {
  id: string;
  name: string;
  entity: string;
  destination: string;
  strategy: string;
  schedule: string;
  lastSynced: string;
  lastSyncedMeta?: string; // e.g. "1,247 rows" or "Running... ~42s" or "Failed 1hr ago"
  status: ScheduleStatus;
  enabled: boolean;
  icon?: string;
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string };
  /** From API: for edit form and payload */
  entityId?: string;
  destinationId?: string;
  destinationType?: string;
  frequency?: string;
  runAt?: string;
  entityName?: string;
  destinationName?: string;
  scheduleConfig?: SchedulePayload;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Human-readable label from schedule config (preferred) or cron string */
function getScheduleLabel(scheduleConfig: SchedulePayload | undefined, cronStr: string): string {
  if (scheduleConfig) {
    const { type, runAt, interval, daysOfWeek, dayOfMonth, timeZone } = scheduleConfig;
    const time = runAt ? runAt.replace(/^(\d{1,2}):(\d{2})$/, (_, h, m) => `${h.padStart(2, '0')}:${m}`) : '';
    const tz = timeZone ? ` ${timeZone}` : '';
    switch (type) {
      case 'Manual':
        return 'Manual only';
      case 'Interval':
        return interval > 0 ? `Every ${interval} min` : 'Interval';
      case 'Daily':
        return time ? `Daily at ${time}${tz}` : 'Daily';
      case 'Weekly':
        if (daysOfWeek.length === 0) return time ? `Weekly at ${time}${tz}` : 'Weekly';
        const days = daysOfWeek.map((d) => DAY_NAMES[d] ?? d).join(', ');
        return time ? `Weekly ${days} at ${time}${tz}` : `Weekly ${days}`;
      case 'Monthly':
        return time ? `Monthly on day ${dayOfMonth} at ${time}${tz}` : `Monthly on day ${dayOfMonth}`;
      case 'Cron':
        return cronStr || 'Custom cron';
      default:
        return cronStr || type || '—';
    }
  }
  if (!cronStr || !cronStr.trim()) return 'Manual only';
  const parts = cronStr.trim().split(/\s+/);
  if (parts.length < 5) return cronStr;
  const [min, hour, dom, month, dow] = parts;
  if (min.startsWith('*/') && hour === '*' && dom === '*' && month === '*' && dow === '*') {
    const n = min.slice(2);
    return `Every ${n} min`;
  }
  if (dom !== '*' && month === '*' && dow === '*') {
    return `${hour}:${min.padStart(2, '0')} on day ${dom} (monthly)`;
  }
  if (dow !== '*' && dom === '*' && month === '*') {
    const days = dow.split(',').map((d) => DAY_NAMES[Number(d)] ?? d).join(', ');
    return `${hour}:${min.padStart(2, '0')} on ${days} (weekly)`;
  }
  if (dom === '*' && month === '*' && dow === '*') {
    return `${hour}:${min.padStart(2, '0')} daily`;
  }
  return cronStr;
}

/** API create/update payload shape */
export interface SchedulerApiPayload {
  name: string;
  entityId: string;
  entityName?: string;
  destinationType: string;
  destinationId: string;
  destinationName?: string;
  strategy: string;
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string };
  schedule: SchedulePayload;
  enabled: boolean;
}

/** Map API schedule response to Schedule (list item). Accepts schedule as string or object; strategyOptions nested or flat. */
function mapApiScheduleToSchedule(item: {
  id: string;
  name: string;
  entityId: string;
  entityName?: string | null;
  destinationType: string;
  destinationId: string;
  destinationName?: string | null;
  strategy: string;
  watermarkColumn?: string | null;
  primaryKey?: string | null;
  strategyOptions?: { watermarkColumn?: string; primaryKey?: string } | null;
  schedule: string | SchedulePayload | unknown;
  frequency?: string;
  runAt?: string;
  enabled: boolean;
}): Schedule {
  const scheduleObj =
    item.schedule != null &&
    typeof item.schedule === 'object' &&
    'type' in item.schedule &&
    'cron' in item.schedule
      ? (item.schedule as SchedulePayload)
      : undefined;
  const scheduleStr =
    typeof item.schedule === 'string'
      ? item.schedule
      : scheduleObj?.cron ?? '';
  const wm = item.watermarkColumn ?? item.strategyOptions?.watermarkColumn;
  const pk = item.primaryKey ?? item.strategyOptions?.primaryKey;
  return {
    id: item.id,
    name: item.name,
    entity: item.entityName || item.entityId,
    destination: item.destinationName || item.destinationType,
    strategy: item.strategy,
    schedule: scheduleStr,
    lastSynced: 'UnKnown',
    status: item.enabled ? 'active' : 'idle',
    enabled: item.enabled,
    icon: '📄',
    strategyOptions: wm || pk ? { watermarkColumn: wm ?? undefined, primaryKey: pk ?? undefined } : undefined,
    entityId: item.entityId,
    destinationId: item.destinationId,
    destinationType: item.destinationType,
    frequency: item.frequency ?? scheduleObj?.type,
    runAt: item.runAt ?? scheduleObj?.runAt,
    entityName: item.entityName ?? undefined,
    destinationName: item.destinationName ?? undefined,
    scheduleConfig: scheduleObj,
  };
}

/** API response item from GET /SchedulerLogs?schedulerId=... */
interface SchedulerLogEntry {
  id: number;
  triggerId?: string;
  streamingId?: string;
  time: string;
  logType: string;
  step?: string;
  operation?: string;
  message: string;
  metadata?: string;
}

interface LogRow {
  id: string;
  triggerId: string;
  time: string;
  timeRaw: string;
  level: 'SUCCESS' | 'INFO' | 'WARN' | 'ERROR';
  message: string;
  step?: string;
  operation?: string;
  metadata?: string | null;
}

function mapSchedulerLogToRow(entry: SchedulerLogEntry): LogRow {
  const level = (['SUCCESS', 'INFO', 'WARN', 'ERROR'].includes(entry.logType)
    ? entry.logType
    : 'INFO') as LogRow['level'];
  const timeStr = entry.time
    ? new Date(entry.time).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    : '—';
  return {
    id: String(entry.id),
    triggerId: entry.triggerId ?? '',
    time: timeStr,
    timeRaw: entry.time ?? '',
    level,
    message: entry.message ?? '',
    step: entry.step,
    operation: entry.operation,
    metadata: entry.metadata ?? null,
  };
}

/** Shorten UUID for display */
function shortId(id: string): string {
  if (!id) return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

interface DataRow {
  id: string;
  email: string;
  name: string;
  segment: string;
  ltv: string;
  updated_at: string;
}

// --- Mock data (used for logs/data only; schedules loaded from API) ---
const MOCK_SCHEDULES: Schedule[] = [];


const MOCK_DATA_ROWS: DataRow[] = [
  { id: 'CU-10041', email: 'alice@acme.io', name: 'Alice Morgan', segment: 'enterprise', ltv: '$12,400', updated_at: '2025-02-25' },
  { id: 'CU-10042', email: 'bob@beta.co', name: 'Bob Chen', segment: 'smb', ltv: '$840', updated_at: '2025-02-25' },
  { id: 'CU-10043', email: 'carol@corp.com', name: 'Carol White', segment: 'enterprise', ltv: '$9,200', updated_at: '2025-02-25' },
  { id: 'CU-10044', email: 'dave@startup.io', name: 'Dave Park', segment: 'trial', ltv: '$0', updated_at: '2025-02-25' },
  { id: 'CU-10045', email: 'eve@mega.org', name: 'Eve Santos', segment: 'mid-market', ltv: '$3,100', updated_at: '2025-02-25' },
  { id: 'CU-10046', email: 'frank@dev.net', name: 'Frank Liu', segment: 'smb', ltv: '$620', updated_at: '2025-02-25' },
  { id: 'CU-10047', email: 'grace@fin.com', name: 'Grace Kim', segment: 'enterprise', ltv: '$28,000', updated_at: '2025-02-25' },
];

// --- Badge / Toggle helpers ---
const statusBadgeClass: Record<ScheduleStatus, string> = {
  active: 'bg-green-500/12 text-green-400',
  running: 'bg-yellow-500/12 text-yellow-400',
  idle: 'bg-zinc-500/15 text-zinc-400',
  error: 'bg-red-500/12 text-red-400',
};

const statusDotClass: Record<ScheduleStatus, string> = {
  active: 'bg-green-500 shadow-[0_0_5px_rgba(34,197,94,0.53)]',
  running: 'bg-yellow-500 animate-pulse',
  idle: 'bg-zinc-500',
  error: 'bg-red-500',
};

const logLevelBorderClass: Record<LogRow['level'], string> = {
  SUCCESS: 'border-l-4 border-l-emerald-500/80',
  INFO: 'border-l-4 border-l-sky-500/60',
  WARN: 'border-l-4 border-l-amber-500/80',
  ERROR: 'border-l-4 border-l-rose-500/80',
};

const logLevelRowClass: Record<LogRow['level'], string> = {
  SUCCESS: 'bg-emerald-500/5 hover:bg-emerald-500/10',
  INFO: 'bg-sky-500/5 hover:bg-sky-500/8',
  WARN: 'bg-amber-500/5 hover:bg-amber-500/10',
  ERROR: 'bg-rose-500/5 hover:bg-rose-500/10',
};

const logLevelTextClass: Record<LogRow['level'], string> = {
  SUCCESS: 'text-emerald-400',
  INFO: 'text-sky-300',
  WARN: 'text-amber-400',
  ERROR: 'text-rose-400',
};

// --- Toggle switch component ---
const Toggle: React.FC<{
  on: boolean;
  onChange: () => void;
  className?: string;
}> = ({ on, onChange, className = '' }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    onClick={onChange}
    className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors flex-shrink-0 ${
      on ? 'bg-indigo-600' : 'bg-neutral-600'
    } ${className}`}
  >
    <span
      className={`absolute top-1 w-3.5 h-3.5 rounded-full bg-white transition-transform ${
        on ? 'left-[18px]' : 'left-1'
      }`}
    />
  </button>
);

const IngestionSchedules = () => {
  const { theme } = useTheme();
  const isLight = theme === 'white';

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [schedulesLoading, setSchedulesLoading] = useState(true);
  const [schedulesError, setSchedulesError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('Add Schedule');
  const [modalSub, setModalSub] = useState('Configure a new automated data sync job');
  const [drawerLogsOpen, setDrawerLogsOpen] = useState(false);
  const [drawerDataOpen, setDrawerDataOpen] = useState(false);
  const [drawerScheduleId, setDrawerScheduleId] = useState<string | null>(null);
  const [drawerScheduleName, setDrawerScheduleName] = useState('--');
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<{ type: 'toggle' | 'delete'; schedule: Schedule } | null>(null);
  const [confirmClearLogsScheduleId, setConfirmClearLogsScheduleId] = useState<string | null>(null);
  const [triggeringScheduleId, setTriggeringScheduleId] = useState<string | null>(null);

  const [logs, setLogs] = useState<LogRow[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [logsSearchQuery, setLogsSearchQuery] = useState('');
  const [logsDateFrom, setLogsDateFrom] = useState('');
  const [logsDateTo, setLogsDateTo] = useState('');
  const [logsTypeFilter, setLogsTypeFilter] = useState<string>('all');
  const [liveLogsOn, setLiveLogsOn] = useState(false);
  const [logsLimit, setLogsLimit] = useState<10 | 100 | 500 | 1000 | 'all'>(100);
  const [dataRows, setDataRows] = useState<DataRow[]>(MOCK_DATA_ROWS);

  useEffect(() => {
    let cancelled = false;
    setSchedulesLoading(true);
    setSchedulesError(null);
    ingestionService.scheduler
      .list()
      .then((list: unknown) => {
        if (cancelled) return;
        const items = Array.isArray(list) ? list : [];
        setSchedules(
          items.map((item: Record<string, unknown>) => {
            const so = item.strategyOptions as { watermarkColumn?: string; primaryKey?: string } | undefined;
            return mapApiScheduleToSchedule({
              id: String(item.id ?? ''),
              name: String(item.name ?? ''),
              entityId: String(item.entityId ?? ''),
              entityName: item.entityName != null ? String(item.entityName) : null,
              destinationType: String(item.destinationType ?? ''),
              destinationId: String(item.destinationId ?? ''),
              destinationName: item.destinationName != null ? String(item.destinationName) : null,
              strategy: String(item.strategy ?? 'Incremental'),
              watermarkColumn: item.watermarkColumn != null ? String(item.watermarkColumn) : so?.watermarkColumn ?? null,
              primaryKey: item.primaryKey != null ? String(item.primaryKey) : so?.primaryKey ?? null,
              strategyOptions: so ?? undefined,
              schedule: item.schedule !== undefined && item.schedule !== null ? item.schedule : '',
              frequency: item.frequency != null ? String(item.frequency) : undefined,
              runAt: item.runAt != null ? String(item.runAt) : undefined,
              enabled: Boolean(item.enabled),
            });
          })
        );
      })
      .catch((err: unknown) => {
        if (!cancelled) setSchedulesError(err instanceof Error ? err.message : 'Failed to load schedules');
      })
      .finally(() => {
        if (!cancelled) setSchedulesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredLogs = useMemo(() => {
    let result = logs;
    if (logsDateFrom.trim()) {
      const from = new Date(logsDateFrom);
      from.setHours(0, 0, 0, 0);
      const fromMs = from.getTime();
      result = result.filter((l) => (l.timeRaw ? new Date(l.timeRaw).getTime() : 0) >= fromMs);
    }
    if (logsDateTo.trim()) {
      const to = new Date(logsDateTo);
      to.setHours(23, 59, 59, 999);
      const toMs = to.getTime();
      result = result.filter((l) => (l.timeRaw ? new Date(l.timeRaw).getTime() : 0) <= toMs);
    }
    if (logsTypeFilter !== 'all') {
      result = result.filter((l) => l.level === logsTypeFilter);
    }
    if (logsSearchQuery.trim()) {
      const q = logsSearchQuery.toLowerCase();
      result = result.filter(
        (l) =>
          l.time.toLowerCase().includes(q) ||
          l.level.toLowerCase().includes(q) ||
          l.message.toLowerCase().includes(q) ||
          (l.step && l.step.toLowerCase().includes(q)) ||
          (l.operation && l.operation.toLowerCase().includes(q)) ||
          (l.triggerId && l.triggerId.toLowerCase().includes(q))
      );
    }
    return result;
  }, [logs, logsSearchQuery, logsDateFrom, logsDateTo, logsTypeFilter]);

  const logsForView = liveLogsOn ? logs : filteredLogs;

  const limitedLogs = useMemo(() => {
    const sorted = [...logsForView].sort((a, b) => (b.timeRaw || '').localeCompare(a.timeRaw || ''));
    if (logsLimit === 'all') return sorted;
    return sorted.slice(0, logsLimit);
  }, [logsForView, logsLimit]);

  const logsByTriggerId = useMemo(() => {
    const map = new Map<string, LogRow[]>();
    for (const log of limitedLogs) {
      const key = log.triggerId || '(no trigger)';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(log);
    }
    const allGroups = Array.from(map.entries());
    allGroups.forEach(([, entries]) => {
      entries.sort((a, b) => (b.timeRaw || '').localeCompare(a.timeRaw || ''));
    });
    const groups = allGroups.map(([triggerId, entries]) => ({
      triggerId,
      entries,
      latest: entries[0]?.timeRaw ?? '',
    }));
    groups.sort((a, b) => b.latest.localeCompare(a.latest));
    return groups;
  }, [limitedLogs]);

  const logStats = useMemo(() => {
    const total = limitedLogs.length;
    const errors = limitedLogs.filter((l) => l.level === 'ERROR').length;
    const warnings = limitedLogs.filter((l) => l.level === 'WARN').length;
    const triggers = new Set(limitedLogs.map((l) => l.triggerId || '').filter(Boolean)).size;
    return { total, errors, warnings, triggers };
  }, [limitedLogs]);

  const clearLogsFilters = () => {
    setLogsDateFrom('');
    setLogsDateTo('');
    setLogsTypeFilter('all');
    setLogsSearchQuery('');
  };

  const exportLogs = () => {
    const headers = ['Time', 'Type', 'Step', 'Operation', 'Message', 'Trigger ID', 'Metadata'];
    const escape = (s: string) => {
      const t = String(s ?? '');
      if (t.includes(',') || t.includes('"') || t.includes('\n') || t.includes('\r')) {
        return `"${t.replace(/"/g, '""')}"`;
      }
      return t;
    };
    const rows = limitedLogs.map((l) =>
      [l.time, l.level, l.step ?? '', l.operation ?? '', l.message, l.triggerId, l.metadata ?? ''].map(escape).join(',')
    );
    const csv = [headers.join(','), ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `schedule-logs-${drawerScheduleName.replace(/[^a-z0-9-_]/gi, '-')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredSchedules = useMemo(() => {
    if (!searchQuery.trim()) return schedules;
    const q = searchQuery.toLowerCase();
    return schedules.filter((s) => s.name.toLowerCase().includes(q));
  }, [schedules, searchQuery]);

  const stats = useMemo(() => {
    const total = schedules.length;
    const active = schedules.filter((s) => s.status === 'active').length;
    const running = schedules.filter((s) => s.status === 'running').length;
    const errors = schedules.filter((s) => s.status === 'error').length;
    return { total, active, running, errors };
  }, [schedules]);

  const triggerScheduleLoad = (id: string, name: string) => {
    if (triggeringScheduleId === id) return;
    setTriggeringScheduleId(id);
    ingestionService.scheduler
      .triggerScheduleLoad(id)
      .then(() => {
        alert(`Schedule "${name}" has been triggered.`);
      })
      .catch((err: unknown) => {
        console.error('Trigger schedule failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to trigger schedule');
      })
      .finally(() => {
        setTriggeringScheduleId(null);
      });
  };

  const toggleScheduleEnabled = (id: string) => {
    const schedule = schedules.find((s) => s.id === id);
    const nextEnabled = schedule ? !schedule.enabled : true;
    ingestionService.scheduler
      .setEnabled(id, nextEnabled)
      .then(() =>
        setSchedules((prev) =>
          prev.map((s) =>
            s.id === id ? { ...s, enabled: nextEnabled, status: nextEnabled ? 'active' : 'idle' } : s
          )
        )
      )
      .catch((err: unknown) => {
        console.error('Toggle schedule failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to update schedule');
      });
  };

  const openScheduleModal = (scheduleId?: string) => {
    setEditingScheduleId(scheduleId ?? null);
    setOverlayOpen(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setOverlayOpen(false);
    setEditingScheduleId(null);
  };

  const refreshSchedules = () => {
    return ingestionService.scheduler.list().then((list: unknown) => {
      const raw = list != null && typeof list === 'object' && 'data' in list ? (list as { data: unknown }).data : list;
      const items = Array.isArray(raw) ? raw : [];
      setSchedules(
        items.map((item: Record<string, unknown>) => {
          const so = item.strategyOptions as { watermarkColumn?: string; primaryKey?: string } | undefined;
          return mapApiScheduleToSchedule({
            id: String(item.id ?? ''),
            name: String(item.name ?? ''),
            entityId: String(item.entityId ?? ''),
            entityName: item.entityName != null ? String(item.entityName) : null,
            destinationType: String(item.destinationType ?? ''),
            destinationId: String(item.destinationId ?? ''),
            destinationName: item.destinationName != null ? String(item.destinationName) : null,
            strategy: String(item.strategy ?? 'Incremental'),
            watermarkColumn: item.watermarkColumn != null ? String(item.watermarkColumn) : so?.watermarkColumn ?? null,
            primaryKey: item.primaryKey != null ? String(item.primaryKey) : so?.primaryKey ?? null,
            strategyOptions: so ?? undefined,
            schedule: item.schedule !== undefined && item.schedule !== null ? item.schedule : '',
            frequency: item.frequency != null ? String(item.frequency) : undefined,
            runAt: item.runAt != null ? String(item.runAt) : undefined,
            enabled: Boolean(item.enabled),
          });
        })
      );
    });
  };

  const handleScheduleSubmit = (data: ScheduleFormValues) => {
    const runAt = data.scheduleConfig?.runAt ?? data.runAt ?? '02:00';
    const [hour = 2, min = 0] = runAt.split(':').map(Number);
    const defaultCron = `${min} ${hour} * * *`;
    const schedule: SchedulePayload = data.scheduleConfig ?? {
      type: data.frequency ?? 'Daily',
      runAt,
      interval: 0,
      daysOfWeek: [],
      dayOfMonth: 0,
      timeZone: 'UTC',
      cron: defaultCron,
    };
    const payload: SchedulerApiPayload = {
      name: data.name,
      entityId: data.entity,
      entityName: data.entityName ?? undefined,
      destinationType: data.destination,
      destinationId: data.destinationId ?? '',
      destinationName: data.destinationName ?? undefined,
      strategy: data.strategy,
      strategyOptions:
        data.strategy === 'Incremental' && data.strategyOptions
          ? {
              watermarkColumn: data.strategyOptions.watermarkColumn ?? undefined,
              primaryKey: data.strategyOptions.primaryKey ?? undefined,
            }
          : undefined,
      schedule,
      enabled: data.enabled,
    };
    const promise = data.id
      ? ingestionService.scheduler.update(data.id, payload)
      : ingestionService.scheduler.create(payload);
    promise
      .then(() => refreshSchedules())
      .then(() => closeModal())
      .catch((err: unknown) => {
        console.error('Schedule save failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to save schedule');
      });
  };

  const fetchLogsForSchedule = (schedulerId: string, showLoading = true) => {
    if (showLoading) setLogsLoading(true);
    ingestionService.scheduler
      .getSchedulerLogs(schedulerId)
      .then((res: unknown) => {
        const raw = res != null && typeof res === 'object' && 'data' in (res as object)
          ? (res as { data: unknown }).data
          : res;
        const arr = Array.isArray(raw) ? raw : [];
        setLogs(arr.map((entry: SchedulerLogEntry) => mapSchedulerLogToRow(entry)));
        setLogsError(null);
      })
      .catch((err: unknown) => {
        console.error('Failed to load scheduler logs:', err);
        setLogsError(err instanceof Error ? err.message : 'Failed to load logs');
        setLogs([]);
      })
      .finally(() => {
        if (showLoading) setLogsLoading(false);
      });
  };

  const openDrawerLogs = (schedulerId: string, name: string) => {
    setDrawerScheduleId(schedulerId);
    setDrawerScheduleName(name);
    setDrawerLogsOpen(true);
    setOverlayOpen(true);
    setLogs([]);
    setLogsError(null);
    fetchLogsForSchedule(schedulerId, true);
  };

  useEffect(() => {
    if (!liveLogsOn || !drawerScheduleId || !drawerLogsOpen) return;
    const LIVE_POLL_INTERVAL_MS = 5000;
    fetchLogsForSchedule(drawerScheduleId, false);
    const id = setInterval(() => {
      fetchLogsForSchedule(drawerScheduleId, false);
    }, LIVE_POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [liveLogsOn, drawerScheduleId, drawerLogsOpen]);

  const openCapturedDataModal = (name: string) => {
    setDrawerScheduleName(name);
    setDrawerDataOpen(true);
    setOverlayOpen(true);
  };

  const closeDrawer = () => {
    setDrawerLogsOpen(false);
    setDrawerDataOpen(false);
    setOverlayOpen(false);
    setDrawerScheduleId(null);
    setLogsSearchQuery('');
    setLogsDateFrom('');
    setLogsDateTo('');
    setLogsTypeFilter('all');
    setLiveLogsOn(false);
  };

  const handleClearLogsConfirm = () => {
    const id = confirmClearLogsScheduleId;
    setConfirmClearLogsScheduleId(null);
    if (!id) return;
    ingestionService.scheduler
      .clearLogs(id)
      .then(() => {
        fetchLogsForSchedule(id, true);
      })
      .catch((err: unknown) => {
        setLogsError(err instanceof Error ? err.message : 'Failed to clear logs');
      });
  };

  const handleOverlayClick = () => {
    closeModal();
    closeDrawer();
  };

  const deleteSchedule = (id: string) => {
    ingestionService.scheduler
      .delete(id)
      .then(() => setSchedules((prev) => prev.filter((s) => s.id !== id)))
      .catch((err: unknown) => {
        console.error('Delete schedule failed:', err);
        alert(err instanceof Error ? err.message : 'Failed to delete schedule');
      });
  };

  const clearLogs = () => {
    if (window.confirm('Clear all log entries?')) setLogs([]);
  };

  const eraseAllData = () => setDataRows([]);

  const removeLog = (id: string) => {
    setLogs((prev) => prev.filter((l) => l.id !== id));
  };

  const removeDataRow = (id: string) => {
    setDataRows((prev) => prev.filter((r) => r.id !== id));
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/65 backdrop-blur-md z-40 transition-opacity duration-200 ${
          overlayOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleOverlayClick}
        aria-hidden
      />

      <div className="w-full px-6 py-8 text-white min-h-screen">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-white">Scheduler</h2>
            <p className="text-sm text-gray-400 mt-0.5">
              Manage automated data sync jobs to your knowledge bases and stores.
            </p>
          </div>
          <button
            type="button"
            onClick={() => openScheduleModal()}
            className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md py-2 px-4 text-sm cursor-pointer transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Schedule
          </button>
        </div>

        <div className="mb-6">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search schedules..."
            className="w-full max-w-[340px] px-4 py-2 text-sm bg-neutral-800 border border-neutral-600 rounded-md text-white placeholder:text-zinc-500 focus:border-indigo-600 outline-none transition-colors"
          />
        </div>

        <div className="grid grid-cols-4 gap-3 mb-6">
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold">{stats.total}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Total</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-green-400">{stats.active}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Active</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-yellow-400">{stats.running}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Running</div>
          </div>
          <div className="bg-neutral-900 border border-neutral-800 rounded-lg p-3">
            <div className="text-xl font-semibold text-red-400">{stats.errors}</div>
            <div className="text-xs text-gray-500 mt-0.5 uppercase tracking-wider">Errors</div>
          </div>
        </div>

        {schedulesError && (
          <p className="text-amber-500 text-sm mb-4">{schedulesError}</p>
        )}
        {schedulesLoading && (
          <p className="text-gray-500 text-sm mb-4">Loading schedules…</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {!schedulesLoading && filteredSchedules.length === 0 && (
            <div className="col-span-full text-center py-12 text-gray-500">
              {schedulesError ? 'Could not load schedules.' : 'No schedules yet. Click Add Schedule to create one.'}
            </div>
          )}
          {filteredSchedules.map((s) => (
            <div
              key={s.id}
              className="bg-neutral-900 border border-neutral-700 rounded-xl p-4 transition-colors hover:border-indigo-600 flex flex-col"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-neutral-800 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 text-sm">
                    {s.icon ?? '📄'}
                  </div>
                  <div>
                    <div className="text-white font-medium text-sm">{s.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {s.entity} → {s.destination}
                    </div>
                  </div>
                </div>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${statusBadgeClass[s.status]}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusDotClass[s.status]}`} />
                  {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                </span>
              </div>
              <div className="space-y-1.5 text-xs text-gray-300 mb-4">
                <div><span className="text-gray-500">Entity:</span> {s.entity}</div>
                <div><span className="text-gray-500">Destination:</span> {s.destination}</div>
                <div><span className="text-gray-500">Strategy:</span> {s.strategy}</div>
                <div><span className="text-gray-500">Schedule:</span> {getScheduleLabel(s.scheduleConfig, s.schedule)}</div>
                <div>
                  <span className="text-gray-500">Last Synced:</span>{' '}
                  {s.status === 'active' && s.lastSyncedMeta ? (
                    <span className="text-green-400">{s.lastSynced} · {s.lastSyncedMeta}</span>
                  ) : s.status === 'running' ? (
                    <span className="text-yellow-400">{s.lastSynced}</span>
                  ) : s.status === 'error' ? (
                    <span className="text-red-400">{s.lastSynced}</span>
                  ) : (
                    <span>{s.lastSynced}{s.lastSyncedMeta ? ` · ${s.lastSyncedMeta}` : ''}</span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-neutral-800">
                <Toggle
                  on={s.enabled}
                  onChange={() => setConfirmState({ type: 'toggle', schedule: s })}
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => openDrawerLogs(s.id, s.name)}
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center"
                    title="Logs"
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => openCapturedDataModal(s.name)}
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center"
                    title="Data"
                  >
                    <Database className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => triggerScheduleLoad(s.id, s.name)}
                    disabled={triggeringScheduleId === s.id}
                    className="p-0.5 text-indigo-400 hover:text-indigo-300 transition-colors inline-flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                    title={triggeringScheduleId === s.id ? 'Triggering…' : 'Run Now'}
                  >
                    <RefreshCw className={`w-4 h-4 ${triggeringScheduleId === s.id ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    type="button"
                    onClick={() => openScheduleModal(s.id)}
                    className="p-0.5 text-yellow-400 hover:text-yellow-300 transition-colors inline-flex items-center"
                    title="Edit"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmState({ type: 'delete', schedule: s })}
                    className="p-0.5 text-red-400 hover:text-red-300 transition-colors inline-flex items-center"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <AddEditScheduleModal
        open={modalOpen}
        onClose={closeModal}
        editSchedule={
          editingScheduleId
            ? (() => {
                const s = schedules.find((x) => x.id === editingScheduleId);
                return s
                  ? {
                      id: s.id,
                      name: s.name,
                      entity: s.entityId ?? s.entity,
                      destination: s.destinationType ?? s.destination,
                      strategy: s.strategy,
                      schedule: s.schedule,
                      enabled: s.enabled,
                      strategyOptions: s.strategyOptions,
                      scheduleConfig: s.scheduleConfig,
                      frequency: s.frequency,
                      runAt: s.runAt,
                      destinationId: s.destinationId,
                      entityName: s.entityName,
                      destinationName: s.destinationName,
                    }
                  : null;
              })()
            : null
        }
        onSubmit={handleScheduleSubmit}
      />

      <ConfirmPopup
        open={!!confirmState && confirmState.type === 'toggle'}
        onClose={() => setConfirmState(null)}
        title={confirmState?.schedule.enabled ? 'Disable schedule?' : 'Enable schedule?'}
        message={
          confirmState?.schedule.enabled
            ? `"${confirmState?.schedule.name}" will be disabled and will not run until turned back on.`
            : `"${confirmState?.schedule.name}" will be enabled and will run according to its schedule.`
        }
        confirmLabel={confirmState?.schedule.enabled ? 'Disable' : 'Enable'}
        variant="primary"
        onConfirm={() => {
          if (confirmState?.type === 'toggle' && confirmState.schedule) {
            toggleScheduleEnabled(confirmState.schedule.id);
          }
          setConfirmState(null);
        }}
      />
      <ConfirmPopup
        open={!!confirmState && confirmState.type === 'delete'}
        onClose={() => setConfirmState(null)}
        title="Delete schedule?"
        message={`"${confirmState?.schedule.name}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => {
          if (confirmState?.type === 'delete' && confirmState.schedule) {
            deleteSchedule(confirmState.schedule.id);
          }
          setConfirmState(null);
        }}
      />
      <ConfirmPopup
        open={!!confirmClearLogsScheduleId}
        onClose={() => setConfirmClearLogsScheduleId(null)}
        title="Clear logs?"
        message="This will remove all log entries for this schedule. This cannot be undone."
        confirmLabel="Clear logs"
        variant="danger"
        onConfirm={handleClearLogsConfirm}
      />

      {/* Logs modal — stats, filters, grouped by trigger */}
      {drawerLogsOpen && (
        <div className="fixed inset-0 z-50 flex flex-col w-full h-full">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={closeDrawer}
            aria-hidden
          />
          <div
            className="relative w-full h-full min-h-full flex flex-col bg-neutral-900 shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-700/80 bg-neutral-900/95 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-neutral-700/60 text-neutral-300">
                  <ScrollText className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Schedule logs</h2>
                  <p className="text-sm text-gray-500 mt-0.5">{drawerScheduleName}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-neutral-800 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stats — light theme uses light bg */}
            <div className={`grid grid-cols-4 gap-3 px-6 py-4 flex-shrink-0 ${isLight ? 'bg-neutral-100 border-b border-neutral-200' : ''}`}>
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/60 border border-neutral-700/60'}`}>
                <div className={`p-2 rounded-lg ${isLight ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-700/80 text-neutral-400'}`}>
                  <FileText className="w-4 h-4" />
                </div>
                <div>
                  <div className={`text-xl font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{logStats.total}</div>
                  <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>Total logs</div>
                </div>
              </div>
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/60 border border-neutral-700/60'}`}>
                <div className="p-2 rounded-lg bg-rose-500/15 text-rose-400">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xl font-semibold text-rose-400">{logStats.errors}</div>
                  <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>Errors</div>
                </div>
              </div>
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/60 border border-neutral-700/60'}`}>
                <div className="p-2 rounded-lg bg-amber-500/15 text-amber-400">
                  <AlertTriangle className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xl font-semibold text-amber-400">{logStats.warnings}</div>
                  <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>Warnings</div>
                </div>
              </div>
              <div className={`flex items-center gap-3 rounded-xl px-4 py-3 ${isLight ? 'bg-white border border-neutral-200' : 'bg-neutral-800/60 border border-neutral-700/60'}`}>
                <div className="p-2 rounded-lg bg-violet-500/15 text-violet-400">
                  <Zap className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-xl font-semibold text-violet-400">{logStats.triggers}</div>
                  <div className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>Triggers</div>
                </div>
              </div>
            </div>

            {/* Filters + Live + Show + Export — light theme uses light bg for count area */}
            <div className={`flex flex-wrap items-center gap-3 px-6 py-3 border-b flex-shrink-0 ${isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-800/20'}`}>
              <button
                type="button"
                onClick={() => setLiveLogsOn((v) => !v)}
                className={`flex items-center gap-2 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                  liveLogsOn
                    ? 'bg-emerald-500/20 border-emerald-500/40 text-emerald-400'
                    : 'bg-neutral-800/80 border-neutral-600 text-neutral-400 hover:text-white hover:border-neutral-500'
                }`}
              >
                <Activity className="w-4 h-4" />
                Live logs
              </button>
              <span className="text-neutral-600">|</span>
              <Filter className="w-4 h-4 text-neutral-500 flex-shrink-0" />
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-neutral-500" />
                <input
                  type="datetime-local"
                  value={logsDateFrom}
                  onChange={(e) => setLogsDateFrom(e.target.value)}
                  disabled={liveLogsOn}
                  className={`px-3 py-2 text-sm rounded-lg border outline-none focus:border-indigo-500 ${
                    liveLogsOn ? 'bg-neutral-800/50 border-neutral-700 text-neutral-500 cursor-not-allowed' : 'bg-neutral-800 border-neutral-600 text-white'
                  }`}
                />
              </div>
              <span className="text-neutral-500 text-sm">to</span>
              <input
                type="datetime-local"
                value={logsDateTo}
                onChange={(e) => setLogsDateTo(e.target.value)}
                disabled={liveLogsOn}
                className={`px-3 py-2 text-sm rounded-lg border outline-none focus:border-indigo-500 ${
                  liveLogsOn ? 'bg-neutral-800/50 border-neutral-700 text-neutral-500 cursor-not-allowed' : 'bg-neutral-800 border-neutral-600 text-white'
                }`}
              />
              <select
                value={logsTypeFilter}
                onChange={(e) => setLogsTypeFilter(e.target.value)}
                disabled={liveLogsOn}
                className={`px-3 py-2 text-sm rounded-lg border outline-none focus:border-indigo-500 min-w-[100px] ${
                  liveLogsOn ? 'bg-neutral-800/50 border-neutral-700 text-neutral-500 cursor-not-allowed' : 'bg-neutral-800 border-neutral-600 text-white'
                }`}
              >
                <option value="all">All types</option>
                <option value="SUCCESS">Success</option>
                <option value="INFO">Info</option>
                <option value="WARN">Warning</option>
                <option value="ERROR">Error</option>
              </select>
              <div className="relative flex-1 min-w-[180px] max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                <input
                  type="text"
                  value={logsSearchQuery}
                  onChange={(e) => setLogsSearchQuery(e.target.value)}
                  disabled={liveLogsOn}
                  placeholder="Search message, step, operation..."
                  className={`w-full pl-9 pr-3 py-2 text-sm rounded-lg border outline-none focus:border-indigo-500 ${
                    liveLogsOn ? 'bg-neutral-800/50 border-neutral-700 text-neutral-500 cursor-not-allowed placeholder:text-neutral-600' : 'bg-neutral-800 border-neutral-600 text-white placeholder:text-gray-500'
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={clearLogsFilters}
                disabled={liveLogsOn}
                className="flex items-center gap-1.5 py-2 px-3 text-xs rounded-lg border border-neutral-600 text-neutral-400 hover:border-neutral-500 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-3.5 h-3.5" /> Clear filters
              </button>
              <div className="flex items-center gap-2 ml-auto">
                <ListFilter className={`w-4 h-4 ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`} />
                <span className={`text-xs ${isLight ? 'text-neutral-600' : 'text-neutral-500'}`}>Show</span>
                <select
                  value={logsLimit}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLogsLimit(v === 'all' ? 'all' : (Number(v) as 10 | 100 | 500 | 1000));
                  }}
                  className={`px-2 py-1.5 text-xs rounded-lg border outline-none focus:border-indigo-500 ${isLight ? 'bg-white border-neutral-300 text-neutral-900' : 'bg-neutral-800 border-neutral-600 text-white'}`}
                >
                  <option value={10}>10</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                  <option value="all">All</option>
                </select>
              </div>
              <span className={`text-xs ${isLight ? 'text-neutral-600' : 'text-neutral-500'}`}>
                {limitedLogs.length} of {logsForView.length} entries
              </span>
              <button
                type="button"
                onClick={exportLogs}
                disabled={limitedLogs.length === 0}
                className={`flex items-center gap-1.5 py-2 px-3 text-xs rounded-lg border font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  isLight
                    ? 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 hover:border-neutral-400'
                    : 'border-neutral-600 text-neutral-300 hover:border-indigo-500 hover:text-white'
                }`}
              >
                <Download className="w-3.5 h-3.5" />  
              </button>
              <button
                type="button"
                onClick={() => drawerScheduleId && setConfirmClearLogsScheduleId(drawerScheduleId)}
                disabled={logsLoading}
                className="flex items-center gap-1.5 py-2 px-3 text-xs rounded-lg border font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-red-500/50 text-red-400 hover:bg-red-500/10 hover:border-red-500"
              >
                <Trash2 className="w-3.5 h-3.5" />  
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
              {logsLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <RefreshCw className="w-8 h-8 text-neutral-400 animate-spin" />
                  <span className="text-sm text-neutral-500">Loading logs…</span>
                </div>
              )}
              {!logsLoading && logsError && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <AlertCircle className="w-10 h-10 text-red-400" />
                  <span className="text-sm text-red-400">{logsError}</span>
                </div>
              )}
              {!logsLoading && !logsError && logsByTriggerId.map(({ triggerId, entries }) => (
                <div key={triggerId} className={`rounded-xl border overflow-hidden ${isLight ? 'border-neutral-200 bg-white' : 'border-neutral-700/80 bg-neutral-800/30'}`}>
                  <div className={`px-4 py-3 border-b flex items-center gap-3 ${isLight ? 'bg-neutral-100 border-neutral-200' : 'bg-neutral-800/60 border-neutral-700/80'}`}>
                    <div className="p-1.5 rounded-lg bg-violet-500/10 text-violet-400">
                      <Zap className="w-3.5 h-3.5" />
                    </div>
                    <span className={`text-xs font-medium uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-500'}`}>Trigger</span>
                    <span className={`text-sm font-mono truncate ${isLight ? 'text-neutral-900' : 'text-white'}`} title={triggerId}>{shortId(triggerId)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${isLight ? 'text-neutral-600 bg-neutral-200' : 'text-neutral-500 bg-neutral-700/60'}`}>
                      {entries.length} log{entries.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className={`border-b ${isLight ? 'text-neutral-600 bg-neutral-100 border-neutral-200' : 'text-neutral-500 border-neutral-700/80 bg-neutral-800/40'}`}>
                          <th className="py-3 px-4 font-medium w-44">Time</th>
                          <th className="py-3 px-4 font-medium w-24">Type</th>
                          <th className="py-3 px-4 font-medium w-28">Step</th>
                          <th className="py-3 px-4 font-medium min-w-[140px]">Operation</th>
                          <th className="py-3 px-4 font-medium">Message</th>
                          <th className="py-3 px-4 font-medium w-16">Meta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((log) => (
                          <tr
                            key={log.id}
                            className={`border-b border-neutral-700/40 transition-colors ${logLevelBorderClass[log.level]} ${logLevelRowClass[log.level]}`}
                          >
                            <td className="py-2.5 px-4 text-neutral-400 font-mono whitespace-nowrap">{log.time}</td>
                            <td className="py-2.5 px-4">
                              {log.level === 'SUCCESS' && <CheckCircle className="w-4 h-4 text-emerald-400 inline-block" />}
                              {log.level === 'INFO' && <Info className="w-4 h-4 text-sky-400 inline-block" />}
                              {log.level === 'WARN' && <AlertTriangle className="w-4 h-4 text-amber-400 inline-block" />}
                              {log.level === 'ERROR' && <AlertCircle className="w-4 h-4 text-rose-400 inline-block" />}
                              <span className={`ml-1.5 ${logLevelTextClass[log.level]}`}>{log.level}</span>
                            </td>
                            <td className="py-2.5 px-4 text-neutral-400">{log.step ?? '—'}</td>
                            <td className="py-2.5 px-4 text-neutral-300">{log.operation ?? '—'}</td>
                            <td className="py-2.5 px-4 text-neutral-300 break-words max-w-md">{log.message}</td>
                            <td className="py-2.5 px-4">
                              {log.metadata ? (
                                <span className="text-neutral-500 cursor-help" title={log.metadata}>JSON</span>
                              ) : (
                                <span className="text-neutral-600">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              {!logsLoading && !logsError && logsByTriggerId.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                  <FileText className="w-12 h-12 text-neutral-600" />
                  <span className="text-sm text-neutral-500">
                    {!liveLogsOn && (logsSearchQuery || logsDateFrom || logsDateTo || logsTypeFilter !== 'all')
                      ? 'No log entries match your filters.'
                      : 'No log entries for this schedule.'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <CapturedDataModal
        open={drawerDataOpen}
        onClose={closeDrawer}
        scheduleName={drawerScheduleName}
        rows={dataRows}
        onEraseAll={eraseAllData}
        onExportCsv={() => {}}
        onDeleteRow={removeDataRow}
      />
    </>
  );
};

export default IngestionSchedules;
